import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Heading } from '../../components';
import Icons from '../../components/Icons/Icons';
import { getAllDiscountPills } from '../../util/listingDiscounts';

import css from './ListingPage.module.css';

/**
 * SectionDiscountsMaybe — "Discounts Available" body section (Listing Richness).
 *
 * Renders the owner's selected discount types as a TEXT list on the listing page,
 * placed between "The Rundown" and "Trailer Details". This replaced the old image
 * OVERLAY on the PDP hero (2026-09-27): only the Haul Star badge stays on the
 * listing image; the itemized discounts live here in the body, and a single
 * generic "Discounts Available" badge lives on the search/feed card image.
 *
 * HAUL STAR only + render-if-present. Lists ALL selected discounts (no cap — the
 * cap only ever applied to image overlays).
 *
 * COMPLIANCE (locked 2026-09-27): PURE DISPLAY / PURE CTA. Each row says the
 * discount is "available" (= ask the owner); never amounts / codes / specifics.
 * BigLoads does NOT honor, transact, or redeem any of it — the renter mentions it
 * when they contact the owner directly. Zero liability.
 *
 * @param {Object} props
 * @param {Object} props.publicData - listing publicData (reads `.discounts`)
 * @param {boolean} props.isHaulStar - whether this listing is on a Haul Star plan
 */
const SectionDiscountsMaybe = props => {
  const { publicData, isHaulStar } = props;

  // Discounts DISPLAY only on Haul Star listings (2026-09-29 decision). ALL
  // owners can SELECT discounts in the create/edit form, but they only render
  // here (and on the search card) for Haul Star listings — the create form shows
  // a note telling non-HS owners exactly that. This makes discounts a Haul Star
  // upsell lure.
  if (!isHaulStar) {
    return null;
  }

  const pills = getAllDiscountPills(publicData?.discounts);
  if (!pills.length) {
    return null;
  }

  return (
    <section className={css.sectionDiscounts}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        <Icons name="discountTag" />
        <FormattedMessage id="ListingPage.discountsHeading" defaultMessage="Discounts Available" />
      </Heading>
      <p className={css.discountsIntro}>
        <FormattedMessage
          id="ListingPage.discountsIntro"
          defaultMessage="This owner offers the discounts below. Ask about them when you reach out — the owner sets and applies the actual savings."
        />
      </p>
      <ul className={css.discountsList}>
        {pills.map(pill => (
          <li className={css.discountsItem} key={pill.key}>
            <span className={css.discountsIcon} aria-hidden="true">
              <Icons name={pill.icon} />
            </span>
            <span className={css.discountsLabel}>{pill.label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default SectionDiscountsMaybe;
